import type {
  PaymentStatus,
  PaymentType,
  PaymentProvider,
} from '@/types/payment';

/**
 * Format payment type - returns translation key for use with useTranslations
 * @param type - Payment type
 * @returns Translation key path (e.g., 'paymentHistory.type.subscription_initial')
 */
export function getPaymentTypeKey(type: PaymentType | null): string {
  if (!type) return 'paymentHistory.type.unknown';
  return `paymentHistory.type.${type}`;
}

/**
 * Get payment status color classes
 * @param status - Payment status
 * @returns Tailwind color classes
 */
export function getPaymentStatusColor(status: PaymentStatus): string {
  const statusColors: Record<PaymentStatus, string> = {
    pending: 'bg-yellow-100 text-yellow-800',
    requires_action: 'bg-warning/15 text-warning',
    processing: 'bg-teal-100 text-teal-800',
    succeeded: 'bg-teal-100 text-teal-800',
    failed: 'bg-red-100 text-red-800',
    canceled: 'bg-muted text-muted-foreground',
    expired: 'bg-muted text-muted-foreground',
    refunded: 'bg-red-100 text-red-800',
    // Entitlement revoked by the store without a money refund.
    revoked: 'bg-red-100 text-red-800',
  };

  return statusColors[status] || 'bg-muted text-muted-foreground';
}

/**
 * Get payment status translation key
 * @param status - Payment status
 * @returns Translation key path (e.g., 'paymentHistory.status.succeeded')
 */
export function getPaymentStatusKey(status: PaymentStatus): string {
  return `paymentHistory.status.${status}`;
}

/**
 * Get payment provider translation key
 * @param provider - Payment provider
 * @returns Translation key path (e.g., 'paymentHistory.provider.stripe')
 */
export function getPaymentProviderKey(
  provider: PaymentProvider | null
): string {
  if (!provider) return 'paymentHistory.provider.unknown';
  return `paymentHistory.provider.${provider}`;
}
